import { withBase } from '@/lib/base-path';

const SESSION_STORAGE_KEY = 'interview_session';
const COMPLETED_HISTORY_KEY = 'interview_completed_history';
const COMPLETED_HISTORY_MAX = 5;
const COMPLETED_HISTORY_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

interface StoredSession {
  sessionId: string;
  flowType: 'lead' | 'client';
  sessionToken?: string;
  timestamp: number;
}

/**
 * Completed-session breadcrumb. Used for:
 *   - Soft dedup on the pre-chat form (same email recently ran a chat? ask).
 *   - Recent-report handoff on the landing page.
 * Stays local to the device — no server-side enumeration risk.
 */
export interface CompletedSessionRecord {
  sessionId: string;
  sessionToken?: string;
  contactEmail?: string;
  completedAt: number;
}

export function saveSessionToStorage(sessionId: string, flowType: 'lead' | 'client', sessionToken?: string): void {
  try {
    const data: StoredSession = {
      sessionId,
      flowType,
      sessionToken,
      timestamp: Date.now(),
    };
    localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(data));
  } catch (error) {
    console.error('Failed to save session to storage:', error);
  }
}

export function getStoredSession(): StoredSession | null {
  try {
    const stored = localStorage.getItem(SESSION_STORAGE_KEY);
    if (!stored) return null;

    const data: StoredSession = JSON.parse(stored);

    // Check if session is less than 24 hours old
    const maxAge = 24 * 60 * 60 * 1000; // 24 hours
    if (Date.now() - data.timestamp > maxAge) {
      clearStoredSession();
      return null;
    }

    return data;
  } catch (error) {
    console.error('Failed to get stored session:', error);
    return null;
  }
}

export function clearStoredSession(): void {
  try {
    localStorage.removeItem(SESSION_STORAGE_KEY);
  } catch (error) {
    console.error('Failed to clear stored session:', error);
  }
}

export function recordCompletedSession(record: Omit<CompletedSessionRecord, 'completedAt'> & { completedAt?: number }): void {
  try {
    const now = Date.now();
    const entry: CompletedSessionRecord = {
      sessionId: record.sessionId,
      sessionToken: record.sessionToken,
      contactEmail: record.contactEmail?.toLowerCase().trim(),
      completedAt: record.completedAt ?? now,
    };
    const existing = getCompletedHistory();
    // Drop any prior record for the same sessionId, then push.
    const deduped = existing.filter((r) => r.sessionId !== entry.sessionId);
    deduped.push(entry);
    deduped.sort((a, b) => b.completedAt - a.completedAt);
    const trimmed = deduped.slice(0, COMPLETED_HISTORY_MAX);
    localStorage.setItem(COMPLETED_HISTORY_KEY, JSON.stringify(trimmed));
  } catch (error) {
    console.error('Failed to record completed session:', error);
  }
}

export function getCompletedHistory(): CompletedSessionRecord[] {
  try {
    const stored = localStorage.getItem(COMPLETED_HISTORY_KEY);
    if (!stored) return [];
    const parsed = JSON.parse(stored);
    if (!Array.isArray(parsed)) return [];
    const cutoff = Date.now() - COMPLETED_HISTORY_TTL_MS;
    const fresh = parsed
      .filter((r: unknown): r is CompletedSessionRecord =>
        typeof r === 'object' && r !== null &&
        typeof (r as { sessionId?: unknown }).sessionId === 'string' &&
        typeof (r as { completedAt?: unknown }).completedAt === 'number'
      )
      .filter((r) => r.completedAt > cutoff);
    if (fresh.length !== parsed.length) {
      localStorage.setItem(COMPLETED_HISTORY_KEY, JSON.stringify(fresh));
    }
    return fresh;
  } catch (error) {
    console.error('Failed to read completed history:', error);
    return [];
  }
}

export function findCompletedSessionByEmail(email: string): CompletedSessionRecord | null {
  const normalized = email.toLowerCase().trim();
  if (!normalized) return null;
  const history = getCompletedHistory();
  return history.find((r) => r.contactEmail === normalized) || null;
}

export function getMostRecentCompleted(): CompletedSessionRecord | null {
  const history = getCompletedHistory();
  return history[0] || null;
}

export async function recoverSession(sessionId: string, sessionToken?: string): Promise<{
  success: boolean;
  session?: {
    id: string;
    status: string;
    flow_type: string;
    contact_name?: string;
    company_name?: string;
  };
  messages?: Array<{
    id: string;
    role: 'assistant' | 'user';
    content: string;
    timestamp: string;
  }>;
  error?: string;
}> {
  try {
    const headers: Record<string, string> = {};
    if (sessionToken) headers['X-Session-Token'] = sessionToken;
    const response = await fetch(withBase(`/api/interview/session/${sessionId}`), { headers });

    if (!response.ok) {
      if (response.status === 404 || response.status === 403) {
        clearStoredSession();
        return { success: false, error: response.status === 403 ? 'Session access denied' : 'Session not found' };
      }
      return { success: false, error: 'Failed to recover session' };
    }

    const data = await response.json();

    // Don't recover completed, abandoned, or paused sessions
    if (['completed', 'abandoned', 'paused'].includes(data.session.status)) {
      clearStoredSession();
      return { success: false, error: data.session.status === 'paused' ? 'Session is paused' : 'Session already completed' };
    }

    return {
      success: true,
      session: data.session,
      messages: data.turns || [],
    };
  } catch (error) {
    console.error('Failed to recover session:', error);
    return { success: false, error: 'Network error' };
  }
}
